import { Loader2 } from 'lucide-react';
import React from 'react';
import { DefaultExtensionType, FileIcon, defaultStyles } from 'react-file-icon';

import { GoogleDrive } from '@/components/icons/GoogleDrive';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface AttachmentProps {
  name: string;
  mime: string;
  previewUrl?: string;
  source?: 'upload' | 'drive';
  /** Present only on placeholder chips: what is happening to the file now. */
  stage?: string;
  children?: React.ReactNode;
}

const Attachment: React.FC<AttachmentProps> = ({
  name,
  mime,
  previewUrl,
  source,
  stage,
  children
}) => {
  const fromDrive = source === 'drive';
  let extension: DefaultExtensionType;
  if (name.includes('.')) {
    extension = name.split('.').pop()!.toLowerCase() as DefaultExtensionType;
  } else {
    extension = mime
      ? ((mime.split('/').pop() || 'txt') as DefaultExtensionType)
      : ('txt' as DefaultExtensionType);
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative h-[58px]">
            {children}
            <Card
              className={cn(
                'h-full p-2 flex flex-row items-center gap-3 rounded-lg w-full max-w-[200px] border',
                fromDrive && 'fongpt-drive-card'
              )}
            >
              {stage ? (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </span>
              ) : previewUrl ? (
                <img
                  src={previewUrl}
                  alt={name}
                  className="h-10 w-10 shrink-0 rounded object-cover"
                />
              ) : fromDrive ? (
                <GoogleDrive className="size-8 shrink-0" />
              ) : (
                <div className="w-10 shrink-0">
                  <FileIcon
                    {...defaultStyles[extension]}
                    extension={extension}
                  />
                </div>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-sm">
                  {name}
                </span>
                {stage ? (
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {stage}
                  </span>
                ) : fromDrive ? (
                  <span className="fongpt-drive-card__label block truncate text-[11px]">
                    Google Drive
                  </span>
                ) : null}
              </span>
            </Card>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{name}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export { Attachment };
